import { el, clear } from '../../core/dom.js';
import { toast } from '../../components/toast.js';
import { empty } from '../../components/empty.js';
import { errorPanel } from '../../components/error.js';
import { button } from '../../components/button.js';
import { skeleton } from '../../components/skeleton.js';
import {
  deleteProviderKey, listModelKeys, listModels, setDefaultModel, setModelEnabled, testProviderKey,
} from './actions.js';
import { armDestructive, resetDestructive } from './confirm.js';
import { PAGE_SIZE, canEnableModel, keySetLabel, pageOf } from './util.js';
import { modelDetailHref } from './model-detail-model.js';

/**
 * @param {{
 *   host: HTMLElement,
 *   isAdmin: boolean,
 *   dead: () => boolean,
 * }} ctx
 */
export function mountModels(ctx) {
  const state = {
    models: [],
    keys: [],
    providers: [],
    defaultModelId: null,
    shown: PAGE_SIZE,
    loading: true,
    fatal: '',
    busyId: '',
  };

  function paint() {
    if (ctx.dead()) return;
    clear(ctx.host);
    if (state.loading) {
      ctx.host.append(skeleton(5));
      return;
    }
    if (state.fatal) {
      ctx.host.append(errorPanel({
        title: 'Models unavailable',
        message: state.fatal,
        action: button('Try again', { variant: 'primary', onClick: () => load(true) }),
      }));
      return;
    }
    ctx.host.append(modelsPanel(state, ctx, {
      onToggle: (model, enabled) => toggle(model, enabled),
      onMore: () => { state.shown += PAGE_SIZE; paint(); },
      onTestKey: (provider, key, done) => testKey(provider, key, done),
      onDeleteKey: (provider, btn) => deleteKey(provider, btn),
      onDefault: (modelId) => saveDefault(modelId),
    }));
  }

  async function toggle(model, enabled) {
    if (!ctx.isAdmin || state.busyId) return;
    const gate = canEnableModel(model, enabled);
    if (!gate.ok) {
      toast(gate.error, 'error');
      paint();
      return;
    }
    state.busyId = model.id;
    paint();
    const result = await setModelEnabled(model.id, enabled);
    if (ctx.dead()) return;
    state.busyId = '';
    if (!result || result.error) {
      toast((result && result.error) || 'Could not update model', 'error');
      await load(false);
      return;
    }
    toast(`${model.label || model.id} ${enabled ? 'enabled' : 'disabled'}`);
    await load(false);
  }

  async function testKey(provider, apiKey, done) {
    const result = await testProviderKey(provider, apiKey);
    if (ctx.dead()) {
      if (done) done(false);
      return;
    }
    if (!result || result.error || result.ok === false) {
      toast((result && result.error) || 'Key test failed', 'error');
      if (done) done(false);
      return;
    }
    const names = (result.enabled_models || []).map((item) => item.label).join(', ') || 'model';
    toast(`${names} enabled`);
    if (done) done(true);
    await load(false);
  }

  async function deleteKey(provider, btn) {
    if (!ctx.isAdmin) return;
    btn.disabled = true;
    const result = await deleteProviderKey(provider);
    if (ctx.dead()) return;
    btn.disabled = false;
    if (!result || result.error) {
      resetDestructive(btn, 'Delete key');
      toast((result && result.error) || 'Could not delete key', 'error');
      return;
    }
    toast('Provider key deleted');
    await load(false);
  }

  async function saveDefault(modelId) {
    if (!ctx.isAdmin || state.busyId) return;
    state.busyId = 'default';
    paint();
    const result = await setDefaultModel(modelId || null);
    if (ctx.dead()) return;
    state.busyId = '';
    if (!result || result.error) {
      toast((result && result.error) || 'Could not set default model', 'error');
      await load(false);
      return;
    }
    state.defaultModelId = result.default_model_id || null;
    toast(result.default_model_id ? 'Default chat model updated' : 'Default chat model cleared');
    paint();
  }

  async function load(initial) {
    if (initial) {
      state.loading = true;
      state.fatal = '';
      paint();
    }
    const [modelsData, keysData] = await Promise.all([listModels(), listModelKeys()]);
    if (ctx.dead()) return;
    state.loading = false;
    if (!modelsData || modelsData.error) {
      state.fatal = (modelsData && modelsData.error) || 'Could not load models';
      state.models = [];
      state.keys = [];
      state.providers = [];
      state.defaultModelId = null;
    } else {
      state.fatal = '';
      state.models = modelsData.models || [];
      state.providers = modelsData.providers || (keysData && keysData.providers) || [];
      state.keys = (keysData && !keysData.error && keysData.keys) || [];
      state.defaultModelId = modelsData.default_model_id || null;
    }
    paint();
  }

  paint();
  load(true);
  return { refresh: () => load(false), dispose() {} };
}

function modelsPanel(state, ctx, handlers) {
  const rows = pageOf(state.models, state.shown);
  return el('div', { class: 'set-stack' },
    defaultPanel(state, ctx, handlers),
    rows.length
      ? el('div', { class: 'set-model-grid' }, rows.map((model) => modelCard(model, state, ctx, handlers)))
      : empty({ title: 'No models available', body: 'The model catalog did not return any entries.' }),
    state.models.length > rows.length
      ? el('div', { class: 'set-panel-foot lbl' },
        el('span', {}, `Showing ${rows.length} of ${state.models.length}`),
        el('button', { type: 'button', class: 'set-more', onClick: handlers.onMore },
          'View all ', el('span', { class: 'set-arw', 'aria-hidden': 'true' }, '→')))
      : null,
    el('p', { class: 'set-lede-soft' },
      'Plan limit reads "— (beta)" until free and paid plans ship. Bring-your-own keys never consume plan allowance. Model choice never changes what a query can see.'),
    keysPanel(state, ctx, handlers));
}

function defaultPanel(state, ctx, handlers) {
  const enabled = (state.models || []).filter((m) => m.enabled);
  const current = state.defaultModelId || '';
  const choices = enabled.slice();
  if (current && !choices.some((m) => m.id === current)) {
    const orphan = (state.models || []).find((m) => m.id === current);
    if (orphan) choices.unshift(orphan);
  }
  const busy = state.busyId === 'default';
  if (!ctx.isAdmin) {
    const label = current
      ? ((choices.find((m) => m.id === current) || {}).label || current)
      : 'Auto (Bonnie picks)';
    return el('div', { class: 'set-panel set-default-model' },
      el('div', { class: 'set-panel-head' },
        el('div', { class: 'set-panel-title' }, el('span', {}, 'Default chat model'))),
      el('div', { class: 'set-byok-body' },
        el('p', {}, label),
        el('p', { class: 'set-hint' }, 'Only admins can change the organization default.')));
  }
  return el('div', { class: 'set-panel set-default-model' },
    el('div', { class: 'set-panel-head' },
      el('div', { class: 'set-panel-title' }, el('span', {}, 'Default chat model'))),
    el('div', { class: 'set-byok-body' },
      el('p', {},
        'Preselected in every chat\'s model picker for this workspace. Members can still pick another model for a single chat.'),
      el('div', { class: 'set-byok-row' },
        el('select', {
          class: 'set-input',
          'aria-label': 'Default chat model',
          disabled: busy ? true : null,
          onChange: (event) => { handlers.onDefault(event.target.value); },
        },
        el('option', { value: '', selected: !current ? true : null }, 'Auto (Bonnie picks)'),
        ...choices.map((model) => el('option', {
          value: model.id,
          selected: model.id === current ? true : null,
        }, model.label || model.id))),
        busy ? el('span', { class: 'set-hint' }, 'Saving…') : null)));
}

function modelCard(model, state, ctx, handlers) {
  const on = !!model.enabled;
  const busy = state.busyId === model.id;
  const isOrgDefault = state.defaultModelId && state.defaultModelId === model.id;
  const detailHref = modelDetailHref(model.id, ctx.productId);
  return el('article', { class: on ? 'set-model is-on' : 'set-model is-off' },
    el('div', { class: 'set-model-head' },
      el('div', { class: 'set-model-title' },
        el('h2', {},
          el('a', { class: 'set-model-link', href: detailHref }, model.label || model.id),
          isOrgDefault ? el('span', { class: 'set-chip' }, 'Org default') : null,
          model.defaultEnabled && !isOrgDefault ? el('span', { class: 'set-chip' }, 'Catalog default') : null,
          model.key_source === 'byok' ? el('span', { class: 'set-chip' }, 'Your key') : null),
        el('p', {}, `${model.providerName || model.provider || ''}${model.whenToUse ? ` · ${model.whenToUse}` : ''}`)),
      ctx.isAdmin
        ? el('label', { class: 'set-switch', title: on ? 'On' : 'Off' },
          el('input', {
            type: 'checkbox',
            checked: on ? true : null,
            disabled: busy ? true : null,
            onChange: (event) => handlers.onToggle(model, !!event.target.checked),
          }),
          el('span', { class: 'set-switch-ui', 'aria-hidden': 'true' }))
        : el('span', { class: 'set-chip' }, on ? 'On' : 'Off')),
    el('div', { class: 'set-model-stats' },
      stat('Queries', String(Number(model.queries || 0).toLocaleString())),
      stat('Tokens', String(Number(model.tokens || 0).toLocaleString())),
      stat('Plan limit', model.plan_limit_label || '— (beta)')),
    el('div', { class: 'set-product-actions' },
      el('a', { class: 'set-btn', href: detailHref }, 'Open detail')));
}

function stat(label, value) {
  return el('div', { class: 'set-stat' },
    el('span', { class: 'lbl' }, label),
    el('span', {}, value));
}

function keysPanel(state, ctx, handlers) {
  const saved = (state.keys || []).length
    ? el('ul', { class: 'set-key-list' }, state.keys.map((key) => {
      const delBtn = ctx.isAdmin
        ? el('button', {
          type: 'button',
          class: 'set-btn set-danger set-key-del',
          onClick: () => armDestructive(delBtn, {
            idle: 'Delete key',
            armed: 'Delete this key',
            onConfirm: () => handlers.onDeleteKey(key.provider, delBtn),
          }),
        }, 'Delete key')
        : null;
      return el('li', { class: 'set-key-row' },
        el('span', { class: 'set-key-meta' },
          el('strong', {}, key.name || key.provider),
          el('span', {}, keySetLabel(key)),
          (key.models || []).length
            ? el('span', { class: 'set-hint' }, ` · enables ${(key.models || []).map((m) => m.label).join(', ')}`)
            : null),
        delBtn);
    }))
    : el('p', { class: 'set-hint' }, 'No provider keys saved yet.');

  const form = { provider: (state.providers[0] && state.providers[0].id) || 'deepseek', key: '' };
  const keyInput = el('input', {
    class: 'set-input',
    type: 'password',
    placeholder: 'Paste API key',
    autocomplete: 'off',
    onInput: (event) => { form.key = event.target.value; },
  });
  const testBtn = el('button', {
    type: 'button',
    class: 'set-btn',
    onClick: async () => {
      if (!ctx.isAdmin) return;
      const apiKey = String(form.key || '').trim();
      if (!form.provider) { toast('Choose a provider', 'error'); return; }
      if (!apiKey) { toast('Paste a key first', 'error'); return; }
      testBtn.disabled = true;
      testBtn.textContent = 'Testing…';
      await handlers.onTestKey(form.provider, apiKey, (ok) => {
        testBtn.disabled = false;
        testBtn.textContent = 'Test & enable';
        if (ok) {
          form.key = '';
          keyInput.value = '';
        }
      });
    },
  }, 'Test & enable');

  return el('div', { class: 'set-panel set-byok' },
    el('div', { class: 'set-panel-head' },
      el('div', { class: 'set-panel-title' }, el('span', {}, 'Bring your own API key'))),
    el('div', { class: 'set-byok-body' },
      el('p', {},
        'Usage is billed by the provider. Keys are stored encrypted. This screen only shows whether a key is set — never the secret value.'),
      el('div', { class: 'set-byok-saved' },
        el('span', { class: 'lbl' }, 'Saved keys'),
        saved),
      ctx.isAdmin
        ? el('div', { class: 'set-byok-form' },
          el('span', { class: 'lbl' }, 'Provider key'),
          el('div', { class: 'set-byok-row' },
            el('select', {
              class: 'set-input',
              'aria-label': 'Provider',
              onChange: (event) => { form.provider = event.target.value; },
            }, ...(state.providers.length
              ? state.providers.map((provider) => el('option', {
                value: provider.id,
                selected: provider.id === form.provider ? true : null,
              }, providerLabel(provider)))
              : [
                el('option', { value: 'deepseek' }, 'DeepSeek'),
                el('option', { value: 'moonshot' }, 'Moonshot AI'),
                el('option', { value: 'anthropic' }, 'Anthropic'),
              ])),
            keyInput,
            testBtn),
          el('p', { class: 'set-hint' },
            'Masked on save, tested before it is trusted. Replacing a key overwrites the saved one for that provider. Delete uses a double confirm.'))
        : el('p', { class: 'set-hint' }, 'Only admins can add, replace, or delete provider keys.')));
}

function providerLabel(provider) {
  const models = (provider.models || []).map((item) => item.label).join(', ');
  return models ? `${provider.name} — ${models}` : (provider.name || provider.id);
}
